import { FileSpreadsheet } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatName } from './prima-nota-archive';
import { readableSize, type PeriodSource } from './prima-nota-model';

type WithdrawFormProps = {
  source: PeriodSource;
  reason: string;
  locked: boolean;
  busy: boolean;
  onReasonChange: (reason: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
};

function WithdrawForm(props: WithdrawFormProps) {
  const { t } = useTranslation('finance');
  const { source, reason, locked } = props;
  return (
    <div className="w-full space-y-2 pt-1">
      <label className="field-label" htmlFor={`withdraw-${source.id}`}>
        {t('primaNota.supply.withdrawPrompt')}
      </label>
      <Input
        id={`withdraw-${source.id}`}
        value={reason}
        onChange={(event) => props.onReasonChange(event.target.value)}
      />
      <p className="text-muted-foreground text-xs">{t('primaNota.supply.withdrawHint')}</p>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={reason.trim().length < 3 || locked}
          onClick={props.onConfirm}
        >
          {t('primaNota.supply.withdrawConfirm')}
        </Button>
        {/* Not while this withdrawal is being sent: the form would close as if
            cancelled, and the withdrawal would still go through. */}
        <Button variant="ghost" size="sm" disabled={props.busy} onClick={props.onCancel}>
          {t('primaNota.supply.withdrawCancel')}
        </Button>
      </div>
    </div>
  );
}

type SourceListProps = {
  sources: PeriodSource[];
  dateFormat: Intl.DateTimeFormat;
  locked: boolean;
  busy: string | null;
  withdrawing: string | null;
  withdrawReason: string;
  onStartWithdraw: (sourceId: string) => void;
  onReasonChange: (reason: string) => void;
  onWithdraw: (sourceId: string) => void;
  onCancelWithdraw: () => void;
};

function SourceRow({ source, ...props }: SourceListProps & { source: PeriodSource }) {
  const { t } = useTranslation('finance');
  const live = source.withdrawnAt === null;
  return (
    <li
      className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2.5 text-sm"
      data-testid={`source-${source.id}`}
    >
      <FileSpreadsheet aria-hidden="true" className="text-muted-foreground size-4" />
      <span
        className={
          live
            ? 'min-w-0 font-medium break-all'
            : 'text-muted-foreground min-w-0 break-all line-through'
        }
      >
        {source.filename}
      </span>
      {source.format !== undefined && source.format !== null && (
        <span className="status-badge" data-testid={`source-format-${source.id}`}>
          {formatName(source.format)}
        </span>
      )}
      <span className="text-muted-foreground text-xs">
        {readableSize(source.byteSize)} ·{' '}
        {t('primaNota.supply.uploadedOn', {
          date: props.dateFormat.format(new Date(source.uploadedAt)),
        })}
      </span>
      {source.withdrawnAt !== null && (
        <span className="text-muted-foreground text-xs">
          {t('primaNota.supply.withdrawn', {
            reason: source.withdrawnReason ?? '',
          })}
        </span>
      )}
      {live && props.withdrawing !== source.id && (
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto"
          disabled={props.locked}
          onClick={() => props.onStartWithdraw(source.id)}
        >
          {t('primaNota.supply.withdraw')}
        </Button>
      )}
      {/* Only over a file still live: after an answer was lost, the re-read may
          show it withdrawn, and a form left open would send the withdrawal again. */}
      {props.withdrawing === source.id && live && (
        <WithdrawForm
          source={source}
          reason={props.withdrawReason}
          locked={props.locked}
          busy={props.busy === source.id}
          onReasonChange={props.onReasonChange}
          onConfirm={() => props.onWithdraw(source.id)}
          onCancel={props.onCancelWithdraw}
        />
      )}
    </li>
  );
}

/** What the month already holds, one line per file, withdrawn ones struck through. */
export function SourceList(props: SourceListProps) {
  const { t } = useTranslation('finance');
  return (
    <div className="space-y-2">
      <h4 className="card-label">{t('primaNota.supply.supplied')}</h4>
      {props.sources.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('primaNota.supply.none')}</p>
      ) : (
        <ul className="divide-border border-border divide-y rounded-md border">
          {props.sources.map((source) => (
            <SourceRow key={source.id} source={source} {...props} />
          ))}
        </ul>
      )}
    </div>
  );
}
